// Auth API surface + AuthProvider for the consumer app.
// Wraps the R2 /auth/* endpoints and exposes the current user, role context,
// and sign-in/out helpers to the role-aware shell.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { api, ApiError } from './api';

export type Locale = 'ar' | 'en';

export interface MeUser {
  id: string;
  email: string | null;
  username: string | null;
  locale: Locale;
  emailVerifiedAt: string | null;
  displayName: string | null;
  // Effective roles from the JWT (M1) — used for staff routing to admin.
  roles: string[];
  currentRoleContext: string | null;
  availableRoleContexts: string[];
  // Admin impersonation: true when an admin is "viewing as" this user (read-only).
  impersonating?: boolean;
  readOnly?: boolean;
}

// ─── API calls ────────────────────────────────────────────────────────────

export async function getMe(signal?: AbortSignal): Promise<MeUser> {
  const { user } = await api<{ user: MeUser }>('/auth/me', { signal });
  return user;
}

export async function loginWithEmail(email: string, password: string): Promise<void> {
  await api('/auth/login', { method: 'POST', body: { email, password } });
}

export async function loginWithUsername(username: string, password: string): Promise<void> {
  await api('/auth/login', { method: 'POST', body: { username, password } });
}

export interface RegisterInput {
  email: string;
  password: string;
  displayName: string;
  locale: Locale;
  marketingOptIn: boolean;
}

export async function register(input: RegisterInput): Promise<void> {
  await api('/auth/register', { method: 'POST', body: input });
}

export async function requestMagicLink(email: string): Promise<void> {
  await api('/auth/magic-link/request', { method: 'POST', body: { email } });
}

export async function logout(): Promise<void> {
  await api('/auth/logout', { method: 'POST' });
}

export async function switchRole(context: string): Promise<void> {
  await api('/auth/role-switch', { method: 'POST', body: { context } });
}

// End an admin read-only impersonation session (clears the cookie).
export async function stopImpersonating(): Promise<void> {
  await api('/auth/impersonate/stop', { method: 'POST' });
}

// ─── Provider ───────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: MeUser | null;
  loading: boolean;
  /** True when /auth/me returned 401 (no valid session). */
  unauthenticated: boolean;
  currentRoleContext: string | null;
  availableRoleContexts: string[];
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  setRole: (context: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const query = useQuery<MeUser, ApiError>({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => getMe(signal),
    staleTime: 60_000,
    retry: (count, err) => {
      // Don't retry a 401 — it's the expected "signed out" state.
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 1;
    },
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    await logout();
    queryClient.setQueryData(['auth', 'me'], null);
    await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
  }, [queryClient]);

  const setRole = useCallback(
    async (context: string) => {
      await switchRole(context);
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    [queryClient],
  );

  const unauthenticated =
    query.isError && query.error instanceof ApiError && query.error.status === 401;
  const user = query.data ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading: query.isLoading,
      unauthenticated,
      currentRoleContext: user?.currentRoleContext ?? null,
      availableRoleContexts: user?.availableRoleContexts ?? [],
      refresh,
      signOut,
      setRole,
    }),
    [user, query.isLoading, unauthenticated, refresh, signOut, setRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an <AuthProvider>');
  return ctx;
}
